import { css, SerializedStyles } from '@emotion/react';
import { Loader } from 'src/domains/layouts/webview/components/loader/Loader';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import styled from '@emotion/styled';
import { withConfig } from 'src/withConfig';
import { DefaultComponentVariantType } from 'src/domains/layouts/config/features/types';

interface SelectionsGroupPropsType {
    variant: DefaultComponentVariantType | null;
}

const setSelectionsGroupLiItem = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            padding: ${theme.star.padding.small};
            border-radius: ${theme.star.radius.medium};
            flex: 1 1 0;
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            padding: ${theme.star.padding.small};
            border-radius: ${theme.star.radius.medium};
            flex: 1 1 0;
        `;
    }
    return css`
        flex: 1 1 auto;
        width: 100%;
    `;
};

export const SelectionsGroupLiItem = withConfig(theme => styled('li', { label: 'SelectionsGroupLiItem' })<SelectionsGroupPropsType>`
    ${({ variant }): SerializedStyles => setSelectionsGroupLiItem(variant, theme)}
    background-color: ${theme.star.selectionsGroup.bgColor};
    color: ${theme.star.selectionsGroup.txtColor};
    display: flex;
    justify-content: space-between;
`);

export const SelectionsGroupName = withConfig(theme => styled('div', { label: 'SelectionsGroupName' })<SelectionsGroupPropsType>`
    align-items: center;
    border-bottom: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? 'none' : variant === DefaultComponentVariantType.TERTIARY ? 'none' : `1px solid ${theme.star.selectionsGroup.borderColor}`};
    display: flex;
    flex: 1 1 auto;
    font-size: ${theme.star.selectionsGroup.typography.fontSizePrimary};
    font-weight: ${theme.star.selectionsGroup.typography.fontWeightPrimary};
    line-height: ${theme.star.selectionsGroup.typography.lineHeightPrimary};
    min-height: 40px;
    padding: 8px 12px;
    width: inherit;
`);

export const SelectionsGroup = withConfig(theme => styled('section', { label: 'SelectionsGroup' })<SelectionsGroupPropsType>`
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    gap: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.gap.small : variant === DefaultComponentVariantType.TERTIARY ? theme.star.gap.small : '0'};
`);

export const SelectionsGroupWrap = withConfig(theme => styled('div', { label: 'SelectionsGroupWrap' })<SelectionsGroupPropsType>`
    border: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? 'none' : variant === DefaultComponentVariantType.TERTIARY ? 'none' : `1px solid ${theme.star.eventRow.borderColor}`};
    border-right: none;
    display: flex;
    flex-flow: column nowrap;
`);

export const SelectionsGroupList = styled('ul', { label: 'SelectionsGroupList' })`
    display: flex;
    flex: 1 1 auto;
    margin: 0;
    padding: 0;
    width: 100%;
    border-right: 0;
    flex-flow: column nowrap;
`;

interface EmptyPropsType {
    selectionsCount: number;
    variant: DefaultComponentVariantType | null;
}

const setEmptyVersion = (theme: EmotionTheme, selectionsCount: number): SerializedStyles => {
    if (selectionsCount > 5) {
        return css `
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            width: 100%;
            @media ${theme.star.mediaQuery.xLargeDesktop} {
                grid-template-columns: repeat(3, 1fr);
            }
        `;
    } else {
        return css `
            display: flex;
            flex-flow: column nowrap;
            width: 100%;
            @media ${theme.star.mediaQuery.tablet} {
                flex-direction: row;
                justify-content: center;
            }
        `;
    }
};

export const Empty = withConfig(theme => styled('div', { label: 'Empty' })<EmptyPropsType>`
    ${({ selectionsCount }): SerializedStyles => setEmptyVersion(theme, selectionsCount)};
    gap: ${({ variant }): string => variant === DefaultComponentVariantType.SECONDARY ? theme.star.gap.small : variant === DefaultComponentVariantType.TERTIARY ? theme.star.gap.small : '0'};
`);

export const MarketLoaderWrapper = styled('div', { label: 'MarketLoaderWrapper' })`
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 8px;
`;

export const MarketLoader = styled(Loader, { label: 'MarketLoader' })`
    fill: currentcolor;
    width: 10px;
`;

interface SelectionGroupListVersionPropsType {
    templateName?: string;
    variant: DefaultComponentVariantType | null;
}

const setListVersion = (theme: EmotionTheme, templateName?: string): SerializedStyles => {
    if (templateName === 'one-column') {
        return css`
            li {
                margin-right: 0 !important; /* Probably to remove */
            }
        `;
    }

    if (templateName === 'two-column') {
        return css`
            display: grid;
            grid-template-columns: 1fr;
            @media ${theme.star.mediaQuery.tablet} {
                grid-template-columns: 1fr 1fr;
            }
        `;
    }

    if (templateName === 'three-column') {
        return css`
            display: grid;
            grid-template-columns: 1fr;
            @media ${theme.star.mediaQuery.tablet} {
                grid-template-columns: 1fr 1fr 1fr;
            }
        `;
    }

    return css``;
};

const setSelectionGroupListVersion = (variant: DefaultComponentVariantType | null, theme: EmotionTheme): SerializedStyles => {
    if (variant === DefaultComponentVariantType.SECONDARY) {
        return css`
            gap: ${theme.star.gap.small};
        `;
    }
    if (variant === DefaultComponentVariantType.TERTIARY) {
        return css`
            gap: ${theme.star.gap.small};
        `;
    }
    return css`
        // border-left: 1px solid ${theme.star.selection.borderColor};
        // margin-left: -1px;
    `;
};

export const SelectionGroupListVersion = withConfig(theme => styled('ul', { label: 'SelectionGroupListVersion' })<SelectionGroupListVersionPropsType>`
    align-self: flex-start;
    display: flex;
    flex-flow: column nowrap;
    width: 100%;
    ${({ templateName }): SerializedStyles => setListVersion(theme, templateName)}; /* This function overwrites default styles above. */
    ${({ variant }): SerializedStyles => setSelectionGroupListVersion(variant, theme)}
`);
